import React from "react";
import { receiptStyles } from "./receiptStyles";

export const money = (value) => Number.isFinite(Number(value)) ? Number(value).toFixed(2) : "0.00";
const present = (value) => value != null && String(value).trim() !== "" && !["null", "undefined", "n/a"].includes(String(value).trim().toLowerCase());
// Older saved receipts include the separately recorded tip among their totals.
// Hide that display row only; leave the saved amounts and item rows untouched.
const isTipTotal = (line) => /^(?:service\s*\/\s*tip|tip\s*\/\s*service|tip)$/i.test(String(line.label ?? "").trim());

/** Presentation only: totals are supplied by the saved bill, never recalculated here. */
export default function BillReceipt({ snapshot = {}, showQr = true }) {
  const { restaurant = {}, bill = {}, sections = [], totals = [], payments = [] } = snapshot;
  const logo = restaurant.logoUrl;
  const visibleTotals = totals.filter(line => !isTipTotal(line));
  return <article className="rd-receipt" aria-label="Restaurant bill">
    <style>{receiptStyles}</style>
    <header className="rd-receipt-header">
      {present(logo) && <img className="rd-receipt-logo" src={logo} alt="Restaurant logo" />}
      <h1>{restaurant.name || "Restaurant"}</h1>
      {present(restaurant.address) && <p>{restaurant.address}</p>}
      {present(restaurant.phone) && <p>Phone: {restaurant.phone}</p>}
      {present(restaurant.website) && <p>{restaurant.website}</p>}
      {present(restaurant.gstNo) && <p>GSTIN: {restaurant.gstNo}</p>}
      {present(restaurant.fssaiNo) && <p>FSSAI: {restaurant.fssaiNo}</p>}
    </header>
    {snapshot.isDraft && <p style={{textAlign:"center",fontWeight:700,border:"1px dashed #555",padding:"1mm",marginBottom:"2mm"}}>Bill preview — not finalized</p>}
    <div className="rd-receipt-identity"><strong>Cash Memo</strong><strong>{bill.billNo ? `Bill ${bill.billNo}` : "Draft"}</strong></div>
    {present(bill.customerName) && <p className="rd-receipt-customer">Customer: <strong>{bill.customerName}</strong></p>}
    <div className="rd-receipt-meta">
      {present(bill.tableNo) && <span>Table: {bill.tableNo}</span>}
      {present(bill.server) && <span>Server: {bill.server}</span>}
      {present(bill.date) && <span>{bill.date}</span>}
      {present(bill.time) && <span>{bill.time}</span>}
    </div>
    {sections.filter(section => section.items?.length > 0).map(section => <section className="rd-receipt-section" key={section.key}>
      <h2>{section.title || (section.key === "wine" ? "Wine Items" : "Food Items")}</h2>
      <table><colgroup><col /><col className="rd-receipt-qty-col" /><col className="rd-receipt-rate-col" /><col className="rd-receipt-amount-col" /></colgroup>
        <thead><tr><th>Item</th><th>Qty</th><th>Rate</th><th>Amount</th></tr></thead>
        <tbody>{section.items.map((item, index) => <tr key={index}>
          <td><strong>{item.name}</strong>{present(item.note) && <small className="rd-receipt-item-note">Note: {item.note}</small>}{present(item.hsnNo) && <small>HSN/SAC: {item.hsnNo}</small>}</td>
          <td>{item.quantity}</td><td>{money(item.rate)}</td><td>{money(item.amount)}</td>
        </tr>)}</tbody>
      </table>
      <div className="rd-receipt-subtotal"><span>{section.key === "wine" ? "Wine" : "Food"} subtotal</span><strong>{money(section.subtotal)}</strong></div>
    </section>)}
    {visibleTotals.length > 0 && <div className="rd-receipt-totals">{visibleTotals.map((line, index) => {
      const rateLabel = /^CGST$/i.test(line.label) || /^SGST$/i.test(line.label) ? " (2.5%)" : "";
      return <div key={index}><span>{line.label}{rateLabel}</span><span>{money(line.amount)}</span></div>;
    })}</div>}
    <div className="rd-receipt-grand"><strong>TOTAL</strong><strong>₹ {money(snapshot.total)}</strong></div>
    {payments.length > 0 && <div className="rd-receipt-payments">{payments.map((line, index) => <div key={index}><span>{line.label}</span><span>₹ {money(line.amount)}</span></div>)}</div>}
    {present(snapshot.amountWords) && <p className="rd-receipt-words">In words: {snapshot.amountWords}</p>}
    {present(snapshot.remark) && <p className="rd-receipt-remark">Note: {snapshot.remark}</p>}
    {showQr && snapshot.qrDataUrl && <div className="rd-receipt-qr"><img src={snapshot.qrDataUrl} alt="Scan to view this bill" /><div><strong>Your bill,<br />on your phone.</strong><p>Scan to view this bill</p></div></div>}
    <footer className="rd-receipt-footer"><strong>Thank you. Visit again!</strong><small>RestroDesk by TDR Software</small></footer>
  </article>;
}
